import {BaseError} from 'viem';
import {DexTxError} from './dex-tx-error';
import {DEX_KNOWN_ERRORS} from './dex-tx-error-naming';
import {ParsedReverDataViem, parseRevertErrorViem} from './revert-error';

/**
 * Base class for all known DEX errors
 */
export abstract class DexKnownError extends DexTxError {
    public override readonly details: Readonly<ParsedReverDataViem>;

    protected constructor(message: string, details: Readonly<ParsedReverDataViem>, options?: ErrorOptions) {
        super(message, details, options);
        this.name = this.constructor.name;
    }

    public static override async fromViem(
        message: string,
        error: BaseError | unknown,
        fallback: boolean = true
    ): Promise<DexTxError | BaseError | Error | unknown> {

        // TODO
        (window as any).dexLastError = {
            message,
            error
        };

        const parsed = await parseRevertErrorViem(error);

        if (parsed) {
            const knownError = DexKnownError.fromParsedData(message, parsed, {cause: error});
            return knownError || new DexTxError(message, parsed, {cause: error});
        }

        return fallback
            ? error instanceof BaseError
                ? error
                : new Error(message, {cause: error})
            : undefined;
    }

    /**
     * Create a specific error instance from parsed revert data
     */
    public static fromParsedData(
        message: string,
        details: Readonly<ParsedReverDataViem>,
        options?: ErrorOptions
    ): DexKnownError | null {
        const errorType = details.name && DEX_KNOWN_ERRORS.hasOwnProperty(details.name)
            ? details.name as keyof typeof DEX_KNOWN_ERRORS
            : undefined;

        if (!errorType) {
            return null;
        }

        // Create the appropriate error instance
        switch (errorType) {
            case 'ERC20InsufficientAllowance':
            case 'PoolTrancheInsufficientAssetAllowance':
                return new DexErc20InsufficientAllowanceError(message, details, options);
            case 'ERC20InsufficientBalance':
            case 'PoolTrancheInsufficientAssetBalance':
                return new DexErc20InsufficientBalanceError(message, details, options);
            case 'PoolTrancheDepositBelowMinValue':
                return new DexPoolTrancheDepositBelowMinValueError(message, details, options);
            case 'PriceStoreNoPriceForDeliverable':
                return new DexPriceStoreNoPriceForDeliverableError(message, details, options);
            default:
                return null;
        }
    }
}

/**
 * ERC20InsufficientAllowance error
 * Error selector: 0xfb8f41b2
 * ABI: error ERC20InsufficientAllowance(address spender, uint256 allowance, uint256 needed)
 */
export class DexErc20InsufficientAllowanceError extends DexKnownError {
    public readonly spender: string;
    public readonly allowance: bigint;
    public readonly needed: bigint;

    constructor(message: string, details: Readonly<ParsedReverDataViem>, options?: ErrorOptions) {
        super(message, details, options);

        // Extract error parameters from argsMap
        this.spender = details.argsMap?.['spender'] || '0x';
        this.allowance = details.argsMap?.['allowance'] || 0n;
        this.needed = details.argsMap?.['needed'] || 0n;
    }

    public override toString(): string {
        return `${this.name}: ${this.message}\n` +
               `  Spender: ${this.spender}\n` +
               `  Allowance: ${this.allowance.toString()}\n` +
               `  Needed: ${this.needed.toString()}\n` +
               `  Shortfall: ${(this.needed - this.allowance).toString()}`;
    }

    public override toJSON(): any {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            spender: this.spender,
            allowance: this.allowance.toString(),
            needed: this.needed.toString(),
            shortfall: (this.needed - this.allowance).toString()
        };
    }
}

/**
 * ERC20InsufficientBalance error
 * Error selector: 0xe450d38c
 * ABI: error ERC20InsufficientBalance(address sender, uint256 balance, uint256 needed)
 */
export class DexErc20InsufficientBalanceError extends DexKnownError {
    public readonly sender: string;
    public readonly balance: bigint;
    public readonly needed: bigint;

    constructor(message: string, details: Readonly<ParsedReverDataViem>, options?: ErrorOptions) {
        super(message, details, options);

        // Extract error parameters from argsMap
        this.sender = details.argsMap?.['sender'] || '0x';
        this.balance = details.argsMap?.['balance'] || 0n;
        this.needed = details.argsMap?.['needed'] || 0n;
    }

    public override toString(): string {
        return `${this.name}: ${this.message}\n` +
               `  Sender: ${this.sender}\n` +
               `  Balance: ${this.balance.toString()}\n` +
               `  Needed: ${this.needed.toString()}\n` +
               `  Shortfall: ${(this.needed - this.balance).toString()}`;
    }

    public override toJSON(): any {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            sender: this.sender,
            balance: this.balance.toString(),
            needed: this.needed.toString(),
            shortfall: (this.needed - this.balance).toString()
        };
    }
}

/**
 * PoolTrancheDepositBelowMinValue error
 * Error selector: 0x7f0a3257
 * ABI: error PoolTrancheDepositBelowMinValue(uint64 poolTrancheId, int64 requiredMinimumValue, int64 valueEntered)
 */
export class DexPoolTrancheDepositBelowMinValueError extends DexKnownError {
    public readonly poolTrancheId: bigint;
    public readonly requiredMinimumValue: bigint;
    public readonly valueEntered: bigint;

    constructor(message: string, details: Readonly<ParsedReverDataViem>, options?: ErrorOptions) {
        super(message, details, options);

        // Extract error parameters from argsMap
        this.poolTrancheId = details.argsMap?.['poolTrancheId'] || 0n;
        this.requiredMinimumValue = details.argsMap?.['requiredMinimumValue'] || 0n;
        this.valueEntered = details.argsMap?.['valueEntered'] || 0n;
    }

    public override toString(): string {
        return `${this.name}: ${this.message}\n` +
               `  Pool Tranche ID: ${this.poolTrancheId.toString()}\n` +
               `  Required Minimum Value: ${this.requiredMinimumValue.toString()}\n` +
               `  Value Entered: ${this.valueEntered.toString()}\n` +
               `  Shortfall: ${(this.requiredMinimumValue - this.valueEntered).toString()}`;
    }

    public override toJSON(): any {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            poolTrancheId: this.poolTrancheId.toString(),
            requiredMinimumValue: this.requiredMinimumValue.toString(),
            valueEntered: this.valueEntered.toString(),
            shortfall: (this.requiredMinimumValue - this.valueEntered).toString()
        };
    }
}

/**
 * PriceStoreNoPriceForDeliverable error
 * Error selector: 0xba390d1d
 * ABI: error PriceStoreNoPriceForDeliverable(uint64 baseDeliverableId, uint64 quoteDeliverableId)
 */
export class DexPriceStoreNoPriceForDeliverableError extends DexKnownError {
    public readonly baseDeliverableId: bigint;
    public readonly quoteDeliverableId: bigint;

    constructor(message: string, details: Readonly<ParsedReverDataViem>, options?: ErrorOptions) {
        super(message, details, options);

        // Extract error parameters from argsMap
        this.baseDeliverableId = details.argsMap?.['baseDeliverableId'] || 0n;
        this.quoteDeliverableId = details.argsMap?.['quoteDeliverableId'] || 0n;
    }

    public override toString(): string {
        return `${this.name}: ${this.message}\n` +
               `  Base Deliverable ID: ${this.baseDeliverableId.toString()}\n` +
               `  Quote Deliverable ID: ${this.quoteDeliverableId.toString()}`;
    }

    public override toJSON(): any {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            baseDeliverableId: this.baseDeliverableId.toString(),
            quoteDeliverableId: this.quoteDeliverableId.toString()
        };
    }
}

/**
 * Type guard to check if an error is a known DEX error
 */
export function isDexKnownError(error: any): error is DexKnownError {
    return error instanceof DexKnownError;
}

/**
 * Type guards for specific error types
 */
export function isDexErc20InsufficientAllowanceError(error: any): error is DexErc20InsufficientAllowanceError {
    return error instanceof DexErc20InsufficientAllowanceError;
}

export function isDexErc20InsufficientBalanceError(error: any): error is DexErc20InsufficientBalanceError {
    return error instanceof DexErc20InsufficientBalanceError;
}

export function isDexPoolTrancheDepositBelowMinValueError(error: any): error is DexPoolTrancheDepositBelowMinValueError {
    return error instanceof DexPoolTrancheDepositBelowMinValueError;
}

export function isDexPriceStoreNoPriceForDeliverableError(error: any): error is DexPriceStoreNoPriceForDeliverableError {
    return error instanceof DexPriceStoreNoPriceForDeliverableError;
}
